import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import { itemMatches } from '../../../config/navConfig';
import { preloadHandlers } from '../../../utils/routePreload';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '../../ui/sidebar';
import OrgSwitcher from './OrgSwitcher';
import NavUser from './NavUser';
import { openAssistant, openCommandPalette, openSupport } from './shellEvents';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

const ACTIONS = {
  assistant: openAssistant,
  support: openSupport,
};

const isItemActive = (item, pathname) =>
  itemMatches(item, pathname) || (item.children || []).some((c) => itemMatches(c, pathname));

const formatBadge = (n) => (n > 99 ? '99+' : String(n));

// Footer utilities (Chat / Admin / Support) sit a step below the nav: smaller
// rows and type, quieter ink, so they don't read as more pages.
const QUIET_ROW = { size: 'sm', className: 'text-[13px] text-sidebar-quiet [&>svg]:size-[15px]' };
const QUIET_SUB_ROW = 'h-[26px] text-[13px] text-sidebar-quiet';

// Gap kept between an opened group and the scroll area's edges.
const REVEAL_PAD = 8;
// Any of these on the scroll area means the user is steering; the reveal backs off.
const USER_SCROLL_EVENTS = ['wheel', 'touchmove', 'pointerdown', 'keydown'];
let stopReveal = () => {};

/**
 * Scrolls the sidebar so a group that just opened is in view. The list rises
 * with the expand animation, frame by frame, until the whole group shows or
 * its own row reaches the top edge. It follows the animation to its end: one
 * scroll at click time stops short (the children haven't grown yet), and so
 * does a fixed time window when a busy page starts the animation late.
 * Opening another group takes over from this one.
 */
function revealOpenedGroup(item, content) {
  const scroller = item?.closest('[data-sidebar="content"]');
  if (!scroller) return;
  stopReveal();

  let frame = 0;
  const stop = () => {
    cancelAnimationFrame(frame);
    USER_SCROLL_EVENTS.forEach((type) => scroller.removeEventListener(type, stop));
  };
  USER_SCROLL_EVENTS.forEach((type) => scroller.addEventListener(type, stop, { passive: true }));
  stopReveal = stop;

  const step = () => {
    const view = scroller.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    const roomAbove = box.top - REVEAL_PAD - view.top;
    const hiddenBelow = box.bottom + REVEAL_PAD - view.bottom;
    let delta = 0;
    if (roomAbove < 0) delta = roomAbove; // its row is cut off at the top: bring it down
    else if (hiddenBelow > 0) delta = Math.min(hiddenBelow, roomAbove); // lift it, never past its own row
    if (Math.abs(delta) >= 1) scroller.scrollTop += delta;

    // Ask every frame rather than awaiting one Animation: Radix re-measures
    // right after opening, which cancels the expand and starts it again. No
    // animation at all (reduced motion) ends this after one step.
    if (content?.getAnimations?.().length) frame = requestAnimationFrame(step);
    else stop();
  };
  frame = requestAnimationFrame(step);
}

/** Leaf row: a link, or an action button (assistant / support). */
function LeafItem({ item, pathname, badge, quiet }) {
  const { setOpenMobile } = useSidebar();
  const Icon = item.icon;
  const rowProps = quiet ? QUIET_ROW : {};
  const content = (
    <>
      {Icon && <Icon />}
      <span>{item.title}</span>
    </>
  );

  return (
    <SidebarMenuItem data-tour={`nav-${item.key}`}>
      {item.action ? (
        <SidebarMenuButton
          {...rowProps}
          tooltip={item.title}
          onClick={() => {
            setOpenMobile(false);
            ACTIONS[item.action]?.();
          }}
        >
          {content}
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton {...rowProps} asChild tooltip={item.title} isActive={itemMatches(item, pathname)}>
          <Link to={item.to} {...preloadHandlers(item.to)} onClick={() => setOpenMobile(false)}>{content}</Link>
        </SidebarMenuButton>
      )}
      {item.badge && (
        <SidebarMenuBadge className="bg-[var(--sidebar-badge-bg)] text-[var(--sidebar-badge-fg)] uppercase tracking-wide">
          {item.badge}
        </SidebarMenuBadge>
      )}
      {badge > 0 && <SidebarMenuBadge>{formatBadge(badge)}</SidebarMenuBadge>}
    </SidebarMenuItem>
  );
}

/**
 * Parent row with sub-items. Expanded sidebar: collapsible list (auto-opens
 * when a child route is active). Icon-only sidebar: a flyout menu to the
 * right, so sub-pages stay one click away (a hover peek shows the list).
 */
function ParentItem({ item, pathname, quiet }) {
  const { state, peek, isMobile, setOpenMobile } = useSidebar();
  const active = isItemActive(item, pathname);
  const [open, setOpen] = useState(active);
  const itemRef = useRef(null);
  const contentRef = useRef(null);
  const wasOpen = useRef(open);
  const Icon = item.icon;
  const rowProps = quiet ? QUIET_ROW : {};

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  // Every open (a click, or a route landing inside the group) scrolls it into view.
  useEffect(() => {
    if (open && !wasOpen.current) revealOpenedGroup(itemRef.current, contentRef.current);
    wasOpen.current = open;
  }, [open]);

  // A hover peek shows the full tree, so only the pinned-collapsed rail uses the flyout.
  if (state === 'collapsed' && !peek && !isMobile) {
    return (
      <SidebarMenuItem data-tour={`nav-${item.key}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton {...rowProps} isActive={active} aria-label={item.title}>
              {Icon && <Icon />}
              <span>{item.title}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" sideOffset={10} className="min-w-52">
            <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {item.children.map((child) => (
              <DropdownMenuItem
                key={child.key}
                asChild
                className={itemMatches(child, pathname) ? 'bg-[var(--lodge-onyx-50)] font-medium' : undefined}
              >
                <Link to={child.to} {...preloadHandlers(child.to)}>{child.title}</Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem ref={itemRef} data-tour={`nav-${item.key}`}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton {...rowProps} tooltip={item.title} isActive={active && !open}>
            {Icon && <Icon />}
            <span>{item.title}</span>
            <ChevronRight
              className={`ml-auto ${quiet ? '!size-3' : '!size-3.5'} text-sidebar-muted transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90`}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent
          ref={contentRef}
          className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down"
        >
          <SidebarMenuSub>
            {item.children.map((child) => {
              const childActive = itemMatches(child, pathname);
              return (
                <SidebarMenuSubItem key={child.key} className="relative">
                  {childActive && (
                    <span aria-hidden className="absolute -left-[11px] top-1 bottom-1 w-0.5 rounded-full bg-sidebar-primary" />
                  )}
                  <SidebarMenuSubButton asChild isActive={childActive} className={quiet ? QUIET_SUB_ROW : undefined}>
                    <Link to={child.to} {...preloadHandlers(child.to)} onClick={() => setOpenMobile(false)}>
                      <span>{child.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavItems({ items, pathname, badges = {}, quiet = false }) {
  return (
    <SidebarMenu className={quiet ? 'gap-0' : undefined}>
      {items.filter((item) => item.sidebar !== false).map((item) =>
        item.children && item.children.length > 0 && !item.sidebarLeaf ? (
          <ParentItem key={item.key} item={item} pathname={pathname} quiet={quiet} />
        ) : (
          <LeafItem key={item.key} item={item} pathname={pathname} badge={badges[item.badgeSource]} quiet={quiet} />
        )
      )}
    </SidebarMenu>
  );
}

function SearchButton() {
  const { isMobile, setOpenMobile } = useSidebar();
  return (
    <SidebarMenu>
      <SidebarMenuItem data-tour="search">
        <SidebarMenuButton
          tooltip="Search"
          onClick={() => {
            if (isMobile) setOpenMobile(false);
            openCommandPalette();
          }}
          className="bg-[var(--sidebar-surface)] text-sidebar-muted shadow-[inset_0_0_0_1px_var(--sidebar-border)] hover:text-[var(--sidebar-strong)]"
        >
          <Search />
          <span>Search</span>
          {!isMobile && (
            <kbd className="ml-auto rounded border border-sidebar-border px-1.5 py-px font-mono text-[11px] text-sidebar-muted group-data-[collapsible=icon]:hidden">
              {isMac ? '⌘K' : 'Ctrl K'}
            </kbd>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Desktop app sidebar — inset variant, collapsible to icons; colours come from the sidebar theme tokens (appShell.css). */
// Phone New look: the sheet opens under the app bar, which keeps working
// while it is open — its menu button closes the sheet itself, so a press
// there must not also count as an outside click (that would reopen it).
const MOBILE_SHEET_PROPS = {
  onInteractOutside: (e) => {
    const target = e.detail?.originalEvent?.target || e.target;
    if (target?.closest?.('[data-mobile-sheet-toggle]')) e.preventDefault();
  },
};

function AppSidebar({ nav, pathname }) {
  const { unreadMessageCount } = useStreamChat();
  const { isMobile } = useSidebar();
  const badges = { chat: unreadMessageCount || 0 };

  return (
    <Sidebar
      variant="inset"
      collapsible="icon"
      mobileClassName="app-mobile-sheet"
      mobileContentProps={MOBILE_SHEET_PROPS}
    >
      <SidebarHeader>
        {/* on phones the org switcher lives in the app bar above the sheet */}
        {!isMobile && <OrgSwitcher />}
        <SearchButton />
      </SidebarHeader>
      <SidebarContent>
        {nav.main.map((group) => (
          <SidebarGroup key={group.key} className="py-1" data-tour={`group-${group.key}`}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <NavItems items={group.items} pathname={pathname} />
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      {/* The hairline marks where the scrolling nav ends and the pinned footer begins. */}
      <SidebarFooter className="gap-1.5 border-t border-sidebar-border">
        <NavItems items={nav.footer} pathname={pathname} badges={badges} quiet />
        <NavUser settings={nav.settings} pathname={pathname} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

// The layout re-renders on every navigation (route, loading bar); the
// sidebar only needs to when the nav tree or the active path changes.
export default React.memo(AppSidebar);
